import { useState } from 'react';
import { useCreateAction, useAction } from '../../hooks/useActions';
import { getServiceLabel } from '../../utils/subscription';

interface RebootPanelProps {
  subscriptionId: string;
  appName: 'UniFi' | 'UISP';
  isMigrated: boolean;
}

export default function RebootPanel({ subscriptionId, appName, isMigrated }: RebootPanelProps) {
  const [activeActionId, setActiveActionId] = useState<string | null>(null);

  const { data: activeAction } = useAction(activeActionId);
  const createAction = useCreateAction();

  const serviceLabel = getServiceLabel(appName, isMigrated);
  const showInfoText = appName === 'UniFi'; // covers both UniFi and UOS (UOS has appName UniFi + isMigrated)

  const handleReboot = async () => {
    try {
      const action = await createAction.mutateAsync({
        subscription_id: subscriptionId,
        action_type: 'reboot',
        payload: {},
      });
      setActiveActionId(action.id);
    } catch (error) {
      console.error('Failed to create reboot action:', error);
    }
  };

  const handleDismiss = () => {
    setActiveActionId(null);
  };

  // Show active action status
  if (activeAction) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        {activeAction.status === 'pending' || activeAction.status === 'claimed' ? (
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            <div>
              <p className="font-medium">Waiting for server...</p>
              <p className="text-sm text-gray-500">Restart queued</p>
            </div>
          </div>
        ) : activeAction.status === 'running' ? (
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            <div>
              <p className="font-medium">Restarting {serviceLabel}...</p>
              <p className="text-sm text-gray-500">{activeAction.result || 'In progress'}</p>
            </div>
          </div>
        ) : activeAction.status === 'completed' ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 rounded-full bg-green-500 flex items-center justify-center">
                <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-green-700">Restart Complete</p>
                <p className="text-sm text-gray-500">{activeAction.result}</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Dismiss
            </button>
          </div>
        ) : activeAction.status === 'failed' ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 rounded-full bg-red-500 flex items-center justify-center">
                <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-red-700">Restart Failed</p>
                <p className="text-sm text-gray-500">{activeAction.result}</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Dismiss
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      {showInfoText && (
        <p className="text-sm text-gray-600 mb-4">
          UniFi network devices will continue to operate normally.
          Hotspot portal will be unavailable during the restart.
        </p>
      )}

      <button
        onClick={handleReboot}
        disabled={createAction.isPending}
        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {createAction.isPending ? 'Starting...' : `Restart ${serviceLabel}`}
      </button>

      {createAction.isError && (
        <p className="mt-2 text-sm text-red-600">
          {(() => {
            const error = createAction.error as { response?: { data?: { message?: string } } };
            return error?.response?.data?.message || (createAction.error as Error)?.message || 'Failed to start restart';
          })()}
        </p>
      )}
    </div>
  );
}
